// compliance.ts — Patch 015.16 (Compliance Calendar).
//
// Backend: workers/core-os/src/routes/compliance.ts (compliance_events
// migration 0069). Org-scoped — no projectId in the paths.

import { request } from './request'

export type ComplianceCategory =
  | 'tax'
  | 'payroll'
  | 'insurance'
  | 'license'
  | 'other'
export type ComplianceRecurrence = 'once' | 'monthly' | 'quarterly' | 'annual'

export interface ComplianceEvent {
  id: string
  title: string
  category: ComplianceCategory
  recurrence: ComplianceRecurrence
  anchor_date: string
  last_completed_at: string | null
  notes: string | null
  next_due_date: string | null
  is_overdue: boolean
  created_at: string
  updated_at: string | null
}

export interface ComplianceListResponse {
  ok: true
  events: ComplianceEvent[]
  count: number
  overdue_count: number
}

export interface CreateComplianceInput {
  title: string
  category: ComplianceCategory
  recurrence: ComplianceRecurrence
  anchor_date: string
  notes?: string
}

// Patch 010.D.11 — compliance_alerts (a distinct entity from the recurring
// compliance_events above): COI/W-9 blockers the daily cron raises.
export interface ComplianceAlert {
  id: string
  project_id: string | null
  contractor_id: string | null
  schedule_task_id: string | null
  alert_type: string
  severity: string
  title: string
  body_text: string | null
  suggested_actions_json: string | null
  status: string
  created_at: string
}

export interface ComplianceAlertsResponse {
  ok: true
  alerts: ComplianceAlert[]
}

export const complianceApi = {
  alerts: (projectId?: string) =>
    request<ComplianceAlertsResponse>(
      `/api/compliance-alerts${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`,
    ),
  list: (category?: ComplianceCategory) =>
    request<ComplianceListResponse>(
      `/api/compliance-events${category ? `?category=${category}` : ''}`,
    ),
  create: (body: CreateComplianceInput) =>
    request<{ ok: true; id: string; request_id: string }>(
      '/api/compliance-events',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (
    id: string,
    body: Partial<CreateComplianceInput> & { archived?: boolean },
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/compliance-events/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  complete: (id: string) =>
    request<{
      ok: true
      id: string
      completed_at: string
      next_due_date: string | null
    }>(`/api/compliance-events/${encodeURIComponent(id)}/complete`, {
      method: 'POST',
      body: '{}',
    }),
}
